/**
 * route_prefetch.js — strategic prefetching for React.lazy routes.
 *
 * Usage:
 *   import { prefetchRoute } from '../lib/route_prefetch';
 *
 *   // 1. Register a path → import factory once at module top-level
 *   prefetchRoute.register('/join-club', () => import('../pages/JoinClubPage'));
 *
 *   // 2. Trigger prefetch on hover / focus / touchstart
 *   <Link to="/join-club"
 *         onMouseEnter={() => prefetchRoute.fetch('/join-club')}
 *         onTouchStart={() => prefetchRoute.fetch('/join-club')}>
 *     Click me
 *   </Link>
 *
 *   // 3. For article pages: high-priority CTA target, then secondaries
 *   prefetchRoute.fetchOnIdle(article.cta_url);              // first (idle)
 *   prefetchRoute.primeAfter(['/', '/blog'], 2500);          // after delay
 *
 * The factory is invoked at most once per path. After that, the chunk is in
 * the browser cache and the subsequent <Suspense> resolves instantly.
 */
const fetched = new Set();
const factories = new Map();

const scheduleIdle = (cb, timeout = 2500) => {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) window.requestIdleCallback(cb, { timeout });
  else setTimeout(cb, 800);
};

export const prefetchRoute = {
  register(path, factory) {
    if (typeof factory !== 'function') return;
    factories.set(path, factory);
  },
  /** Immediate prefetch (chunk download starts right away). */
  fetch(path) {
    if (!path || fetched.has(path)) return;
    const f = factories.get(path);
    if (!f) return;
    fetched.add(path);
    try { f(); } catch { fetched.delete(path); }
  },
  /** Prefetch as soon as the browser is idle (does not block first paint). */
  fetchOnIdle(path) {
    if (!path) return;
    scheduleIdle(() => this.fetch(path));
  },
  /** Prefetch a batch during idle (used to warm up funnel pages after Home loads). */
  prime(paths) {
    if (typeof window === 'undefined') return;
    scheduleIdle(() => paths.forEach((p) => this.fetch(p)));
  },
  /** Prefetch a batch after a fixed delay (used after page content has rendered). */
  primeAfter(paths, ms = 2500) {
    if (typeof window === 'undefined') return;
    setTimeout(() => this.prime(paths), ms);
  },
  /** Match a URL against dynamic patterns like `/blog/:slug` or `/blog/*` and
   *  return the winning path key (or null). Query/hash are stripped. */
  match(rawPath) {
    if (!rawPath || typeof rawPath !== 'string') return null;
    const clean = rawPath.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
    if (factories.has(clean)) return clean;
    // Try wildcard/param matchers registered as e.g. `/blog/*` or `/blog/:slug`
    const parts = clean.split('/').filter(Boolean);
    let best = null;
    for (const key of factories.keys()) {
      if (!key.includes(':') && !key.includes('*')) continue;
      const patParts = key.split('/').filter(Boolean);
      if (patParts.length !== parts.length) continue;
      const ok = patParts.every((p, i) => p === '*' || p.startsWith(':') || p === parts[i]);
      if (ok) { best = key; break; }
    }
    return best;
  },
};

/** Global auto-prefetch: attach hover/focus/touchstart listeners at the
 *  document level so EVERY `<Link>` and `<a>` on the site benefits without
 *  per-component instrumentation. Call once from the App root inside useEffect. */
export const attachGlobalPrefetch = () => {
  if (typeof window === 'undefined' || attachGlobalPrefetch._attached) return () => {};
  attachGlobalPrefetch._attached = true;

  const anchorPath = (el) => {
    if (!el || el.tagName !== 'A') return null;
    const href = el.getAttribute('href');
    if (!href || /^(https?:|mailto:|tel:|#)/.test(href)) return null;
    try {
      const u = new URL(href, window.location.origin);
      return u.origin === window.location.origin ? u.pathname : null;
    } catch { return null; }
  };

  const findAnchor = (t) => {
    let el = t;
    while (el && el.nodeType === 1) {
      if (el.tagName === 'A') return el;
      el = el.parentNode;
    }
    return null;
  };

  const onHover = (e) => {
    const a = findAnchor(e.target);
    const p = anchorPath(a);
    if (!p) return;
    // Try exact match first, then dynamic pattern match
    const key = factories.has(p) ? p : prefetchRoute.match(p);
    if (key) prefetchRoute.fetch(key);
  };

  document.addEventListener('mouseover', onHover, { passive: true, capture: true });
  document.addEventListener('focusin', onHover, { passive: true, capture: true });
  document.addEventListener('touchstart', onHover, { passive: true, capture: true });

  return () => {
    document.removeEventListener('mouseover', onHover, { capture: true });
    document.removeEventListener('focusin', onHover, { capture: true });
    document.removeEventListener('touchstart', onHover, { capture: true });
    attachGlobalPrefetch._attached = false;
  };
};

export default prefetchRoute;
